import { useState, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/apiFetch";

const SUPPLIERS = ["EB Horsman", "Gescan", "City Electric", "Home Depot", "Other"];

const CATEGORY_RULES = [
  // Generators + Generac line (standby gens, SMM modules, maint kits, ATS,
  // transfer switches, air-cooled accessory kits). Placed first so it claims
  // items that would otherwise fall into Switches & Controls / E&A / Misc.
  { keywords: ["GENERATOR", "GENERAC", "STANDBY", "AIR-COOLED", "AIR COOLED", "MANAGEMENT MODULE", "SMM ", "MAINT.KIT", "MAINTENANCE KIT", "TRANSWITCH", "TRANSFER SWITCH", "ATS-"], category: "Generators", unit: "each" },
  // Mini Splits (Perfect Aire + accessories — line sets, HVAC cable, condenser
  // brackets, quick-connect kits). Also placed up front so the HVAC cable and
  // line-set covers don't get pulled into Wire & Cable / Misc.
  { keywords: ["MINI SPLIT", "MINI-SPLIT", "MINISPLIT", "PERFECT AIRE", "PAMSH", "LINE SET", "LSKIT", "HVAC CABLE", "MA-SB"], category: "Mini Splits", unit: "each" },
  { keywords: ["NMD", "TECK", "ACWU", "RW90", "AC90", "WELDING CABLE", "LVT ", "SEALTITE", "LOOMEX", "ALUM FLEX"], category: "Wire & Cable", unit: "m" },
  { keywords: ["EMT CONDUIT", "RIGID CONDUIT", "PVC CONDUIT", "DBII BEND", "DBII LONG"], category: "Conduit", unit: "ft" },
  { keywords: ["BREAKER", "LOADCENTRE", "LOAD CENTER", "BRKR", "METER MAIN", "MTR BASE", "PANELBOARD"], category: "Breakers & Panels", unit: "each" },
  { keywords: ["GFI", "GFCI", "RECEPTACLE", "RECEPT", "PLUG"], category: "Devices & Receptacles", unit: "each" },
  { keywords: ["SWITCH", "DIMMER", "SELECTOR", "TIMER"], category: "Switches & Controls", unit: "each" },
  { keywords: ["DISK LIGHT", "HIGHBAY", "HIGH BAY", "LAMP", " LED", "CFL", "FIXTURE"], category: "Lighting", unit: "each" },
  { keywords: ["LOCKNUT", "BUSHING", "STRAP", "CLAMP", "NIPPLE", "REDUCER", "COUPLING", "OFFSET", "ELBOW"], category: "Conduit Fittings & Hardware", unit: "each" },
  { keywords: ["CONNECTOR", "TECK CONN", "FLEX CONN", "CABLE CONN", "SEALING RING", "NON-METALLIC CONN", "COMP CONN"], category: "Cable Connectors", unit: "each" },
  { keywords: ["SPLICE", "LUG", "SPLIT BOLT", "BOND", "GROUND"], category: "Grounding & Bonding", unit: "each" },
  { keywords: [" BOX", "ENCLOSURE", "EXTENSION RING"], category: "Boxes & Enclosures", unit: "each" },
  { keywords: ["COVER", "PLATE", "BLANK", "QUICKPORT"], category: "Covers & Plates", unit: "each" },
  { keywords: ["SCREW", "BOLT", "ANCHOR", "WASHER", "STAPLE", "SPRING NUT", "STRUT"], category: "Fasteners & Strut", unit: "each" },
  { keywords: ["CABLE TRAY", "LADDER TRAY"], category: "Cable Tray", unit: "each" },
  { keywords: ["FAN", "THERMOSTAT", "HEATING", "BATTERY", "SMOKE", "ALARM", "DISCONNECT", "BASEBOARD", "HEATER"], category: "Equipment & Accessories", unit: "each" },
  { keywords: ["TAPE", "SPRAY", "PAINT", "CEMENT", "OXIDE", "DUCT SEAL", "VAPOR BARRIER", "REEL", "MARKING"], category: "Tools & Supplies", unit: "each" },
];

function categorize(sku, desc) {
  const s = (sku + " " + desc).toUpperCase();
  for (const rule of CATEGORY_RULES) {
    if (rule.keywords.some((k) => s.includes(k))) {
      return { category: rule.category, unit: rule.unit };
    }
  }
  return { category: "Misc", unit: "each" };
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result.split(",")[1]);
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

// PDF route: Claude extracts a flat rows array, which we then reshape into
// the same { headers, rows } structure parseCSV returns so the rest of the
// import flow (column map → preview → upsert) works unchanged.
const PDF_HEADERS = ["SKU", "Description", "Unit Price", "Quantity", "Total"];

async function parsePDFviaClaude(file) {
  const base64 = await readFileAsBase64(file);
  const response = await apiFetch("/api/claude", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-6",
      max_tokens: 8192,
      messages: [{
        role: "user",
        content: [
          { type: "document", source: { type: "base64", media_type: "application/pdf", data: base64 } },
          {
            type: "text",
            text: `Extract every product line item from this supplier price list / invoice / quote PDF.

Return ONLY a JSON array — no prose, no markdown fences. Each element must have these exact keys:
  "sku"          — supplier item / catalog / part number (string)
  "description"  — product description (string, may be empty)
  "unit_price"   — price per unit as a number (no currency symbol, no commas)
  "quantity"     — quantity if shown, otherwise null
  "total"        — line total if shown, otherwise null

Rules:
- Include every row that has a SKU and a price. Skip headers, subtotals, taxes, freight, and summary lines.
- If only a line total + quantity is shown (no unit price), still include the row with unit_price = total/quantity.
- Preserve SKUs exactly as printed (case, dashes, dots).
- If the PDF has multiple pages, extract from all pages.
- Numbers only for unit_price / quantity / total — never strings like "$1,234.56".

Output format example:
[{"sku":"NMD90-14-2","description":"14/2 NMD90 75M","unit_price":0.4523,"quantity":75,"total":33.92}]`,
          },
        ],
      }],
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error?.message || data?.error || "Claude PDF extraction failed");
  }
  const text = data.content?.[0]?.text || "[]";

  // Be lenient: strip code fences, find the first [...] block.
  let cleaned = text.replace(/```json|```/g, "").trim();
  const match = cleaned.match(/\[[\s\S]*\]/);
  if (match) cleaned = match[0];

  let items;
  try {
    items = JSON.parse(cleaned);
  } catch {
    throw new Error("Could not parse PDF extraction — try a CSV export instead");
  }
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error("No line items found in this PDF");
  }

  const rows = items.map((it) => ({
    "SKU": String(it.sku ?? "").trim(),
    "Description": String(it.description ?? "").trim(),
    "Unit Price": it.unit_price == null ? "" : String(it.unit_price),
    "Quantity": it.quantity == null ? "" : String(it.quantity),
    "Total": it.total == null ? "" : String(it.total),
  }));

  return { headers: PDF_HEADERS, rows };
}

function parseCSV(text) {
  const lines = text.split(/\r?\n/);
  const headers = lines[0].split(",").map((h) => h.replace(/"/g, "").trim());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    const cols = [];
    let cur = "", inQ = false;
    for (const ch of lines[i]) {
      if (ch === '"') { inQ = !inQ; continue; }
      if (ch === "," && !inQ) { cols.push(cur.trim()); cur = ""; continue; }
      cur += ch;
    }
    cols.push(cur.trim());
    const row = {};
    headers.forEach((h, idx) => { row[h] = cols[idx] || ""; });
    rows.push(row);
  }
  return { headers, rows };
}

function detectColumns(headers) {
  const lower = headers.map((h) => h.toLowerCase());
  const find = (...terms) => {
    for (const t of terms) {
      const idx = lower.findIndex((h) => h.includes(t));
      if (idx !== -1) return headers[idx];
    }
    return null;
  };
  return {
    sku: find("item", "sku", "part", "product", "code"),
    desc: find("description", "desc", "name"),
    price: find("unit price", "unit_price", "price/unit", "price per", "cost", "price"),
    qty: find("qty", "quantity", "total qty"),
    total: find("total amount", "amount", "total"),
  };
}

function parsePrice(val) {
  if (!val) return null;
  const cleaned = String(val).replace(/[$,\s]/g, "");
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

// ─── AI MATCH via Claude Proxy ───────────────────────────────────────────────
async function aiMatchProducts(newProducts, existingProducts) {
  const response = await apiFetch("/api/claude", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-6",
      max_tokens: 1000,
      messages: [{
        role: "user",
        content: `You are matching supplier products to a master product database for an electrical supply company.

For each NEW product, find the best match in EXISTING products based on:
1. Electrical specs (cable type, conductors, gauge, voltage, size, amperage)
2. Product type and function
3. Key identifiers that MUST match exactly: conductor count, wire gauge, voltage rating, product type

NEVER match products with different voltage ratings (600V vs 1000V) or different conductor counts.

NEW PRODUCTS (to match):
${JSON.stringify(newProducts.map(p => ({ sku: p.sku, desc: p.desc })), null, 2)}

EXISTING PRODUCTS (match against these):
${JSON.stringify(existingProducts.map(p => ({ id: p.id, sku: p.sku, desc: p.description })), null, 2)}

Respond ONLY with a JSON array, no markdown, no explanation:
[
  {
    "new_sku": "SKU from new product",
    "match_id": "UUID of best existing match or null if no confident match",
    "confidence": 0-100,
    "reason": "brief reason"
  }
]`
      }]
    })
  });
  const data = await response.json();
  const text = data.content?.[0]?.text || "[]";
  try {
    return JSON.parse(text.replace(/```json|```/g, "").trim());
  } catch {
    return [];
  }
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function PriceImport() {
  const [step, setStep] = useState("upload");
  const [supplier, setSupplier] = useState("");
  const [customSupplier, setCustomSupplier] = useState("");
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [colMap, setColMap] = useState({});
  const [preview, setPreview] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [importStats, setImportStats] = useState(null);
  const [error, setError] = useState(null);
  const [log, setLog] = useState([]);
  const fileRef = useRef();

  const supplierName = supplier === "Other" ? customSupplier : supplier;

  const addLog = (msg, type = "info") => {
    setLog((prev) => [...prev, { msg, type, ts: Date.now() }]);
  };

  const handleFile = useCallback(async (f) => {
    if (!f) return;
    const isPDF = /\.pdf$/i.test(f.name) || f.type === "application/pdf";
    const isCSV = /\.csv$/i.test(f.name);
    if (!isPDF && !isCSV) {
      setError("Please upload a CSV or PDF file.");
      return;
    }
    setFile(f);
    setError(null);

    if (isPDF) {
      setStep("extracting");
      try {
        const { headers, rows } = await parsePDFviaClaude(f);
        const detected = detectColumns(headers);
        setParsed({ headers, rows });
        setColMap(detected);
        setStep("map");
      } catch (err) {
        setError(err.message || "Could not extract data from PDF");
        setStep("upload");
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers, rows } = parseCSV(e.target.result);
      const detected = detectColumns(headers);
      setParsed({ headers, rows });
      setColMap(detected);
      setStep("map");
    };
    reader.readAsText(f);
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const buildPreview = () => {
    if (!parsed || !colMap.sku || !colMap.price) {
      setError("Please map at least SKU and Price columns.");
      return;
    }
    setError(null);
    const seen = new Map();
    const items = [];

    for (const row of parsed.rows) {
      const sku = row[colMap.sku]?.trim();
      if (!sku) continue;

      let price = parsePrice(row[colMap.price]);

      if (price === null && colMap.total && colMap.qty) {
        const total = parsePrice(row[colMap.total]);
        const qty = parsePrice(row[colMap.qty]);
        if (total && qty && qty > 0) price = total / qty;
      }

      if (price === null || price <= 0 || !isFinite(price)) continue;

      const desc = colMap.desc ? row[colMap.desc]?.trim() || "" : "";
      const { category, unit } = categorize(sku, desc);

      if (seen.has(sku)) {
        const existing = seen.get(sku);
        existing.prices.push(price);
        existing.unit_price = existing.prices.reduce((a, b) => a + b, 0) / existing.prices.length;
      } else {
        const item = { sku, desc, category, unit, unit_price: price, prices: [price] };
        seen.set(sku, item);
        items.push(item);
      }
    }

    setPreview(items.map((i) => ({ ...i, unit_price: Math.round(i.unit_price * 10000) / 10000 })));
    setStep("preview");
  };

  const runImport = async () => {
    setStep("importing");
    setLog([]);
    const stats = { total: preview.length, matched: 0, new: 0, updated: 0, flagged: 0 };

    try {
      addLog("Loading existing products from database...");
      // Fetch the FULL catalog. A single Supabase select caps at 1000 rows, so
      // on a large master_products table the dedup below would miss existing
      // SKUs and try to re-insert them — hitting master_products_sku_key. Page
      // through until we've read everything.
      const existing = [];
      const FETCH_PAGE = 1000;
      for (let from = 0; ; from += FETCH_PAGE) {
        const { data: page, error: fetchErr } = await supabase
          .from("master_products")
          .select("id, sku, description, category, unit")
          .range(from, from + FETCH_PAGE - 1);
        if (fetchErr) throw fetchErr;
        existing.push(...(page || []));
        if (!page || page.length < FETCH_PAGE) break;
      }

      const { data: aliases } = await supabase
        .from("supplier_aliases")
        .select("master_product_id, supplier_sku, supplier_name")
        .eq("supplier_name", supplierName);

      const aliasMap = new Map((aliases || []).map((a) => [a.supplier_sku, a.master_product_id]));
      const skuMap = new Map(existing.map((p) => [p.sku.toUpperCase(), p]));
      const byId = new Map(existing.map((p) => [p.id, p])); // master_id → existing row, for category-backfill checks

      addLog(`Found ${existing.length} existing products, ${aliasMap.size} known aliases for ${supplierName}`);

      const matched = [], needsAI = [];

      for (const item of preview) {
        if (aliasMap.has(item.sku)) {
          matched.push({ ...item, master_id: aliasMap.get(item.sku), matchType: "alias" });
          continue;
        }
        const exact = skuMap.get(item.sku.toUpperCase());
        if (exact) {
          matched.push({ ...item, master_id: exact.id, matchType: "exact" });
          continue;
        }
        needsAI.push(item);
      }

      addLog(`${matched.length} direct matches, ${needsAI.length} need AI matching`);

      const aiMatched = [], stillNew = [];
      if (needsAI.length > 0) {
        addLog("Running AI matching...");
        const BATCH = 20;
        for (let i = 0; i < needsAI.length; i += BATCH) {
          const batch = needsAI.slice(i, i + BATCH);
          const relevant = existing.slice(0, 100);
          const results = await aiMatchProducts(batch, relevant);

          for (const res of results) {
            const item = batch.find((b) => b.sku === res.new_sku);
            if (!item) continue;
            if (res.match_id && res.confidence >= 75) {
              aiMatched.push({ ...item, master_id: res.match_id, matchType: "ai", confidence: res.confidence });
            } else if (res.confidence >= 50 && res.confidence < 75) {
              stats.flagged++;
              stillNew.push({ ...item, flagged: true, aiReason: res.reason });
            } else {
              stillNew.push({ ...item, flagged: false });
            }
          }
          addLog(`AI batch ${Math.ceil((i + 1) / BATCH)} done`);
        }
      }

      const allMatched = [...matched, ...aiMatched];

      // Final duplicate guard before inserting. Anything whose SKU already
      // exists (case-insensitively) is NOT a new product — route it to a price
      // update on the existing row and record what it matched, so we never hit
      // master_products_sku_key and the user can see exactly what duplicated.
      // Also de-dupe within this file (two case-variants of one SKU).
      const duplicates = [];
      const trulyNew = [];
      const insertSeen = new Set();
      for (const p of stillNew) {
        const key = p.sku.toUpperCase();
        const existingRow = skuMap.get(key);
        if (existingRow) {
          allMatched.push({ ...p, master_id: existingRow.id, matchType: "exact" });
          duplicates.push({ sku: p.sku, desc: p.desc, unit_price: p.unit_price, matchedSku: existingRow.sku, matchedDesc: existingRow.description || "" });
          continue;
        }
        if (insertSeen.has(key)) {
          duplicates.push({ sku: p.sku, desc: p.desc, unit_price: p.unit_price, matchedSku: p.sku, matchedDesc: "(repeated earlier in this file)" });
          continue;
        }
        insertSeen.add(key);
        trulyNew.push(p);
      }
      stats.duplicates = duplicates.length;
      stats.duplicateRows = duplicates;

      if (duplicates.length > 0) {
        addLog(`${duplicates.length} duplicate SKU${duplicates.length === 1 ? "" : "s"} already in the price book — refreshing their price instead of adding new rows`);
      }

      if (trulyNew.length > 0) {
        addLog(`Inserting ${trulyNew.length} new product${trulyNew.length === 1 ? "" : "s"}...`);
        const toInsert = trulyNew.map((p) => ({
          sku: p.sku,
          description: p.desc || p.sku,
          category: p.category,
          unit: p.unit,
        }));
        const { data: inserted, error: insertErr } = await supabase
          .from("master_products")
          .insert(toInsert)
          .select("id, sku");
        if (insertErr) throw insertErr;
        for (const ins of inserted) {
          const orig = trulyNew.find((p) => p.sku === ins.sku);
          if (orig) allMatched.push({ ...orig, master_id: ins.id, matchType: "new" });
        }
        stats.new = inserted.length;
      }

      // Backfill category on existing master_products rows that were stamped
      // "Misc" by an older rule set. We only overwrite Misc — never stomp on
      // a category that's already specific (could be a manual override).
      const toRecat = [];
      for (const m of allMatched) {
        if (m.matchType === "new") continue;
        const row = byId.get(m.master_id);
        if (!row) continue;
        const currentCat = (row.category || "").trim();
        if (currentCat && currentCat !== "Misc") continue;
        if (m.category && m.category !== "Misc") {
          toRecat.push({ id: m.master_id, category: m.category, unit: m.unit });
        }
      }
      if (toRecat.length > 0) {
        addLog(`Re-categorizing ${toRecat.length} existing products that were tagged Misc...`);
        for (const r of toRecat) {
          const { error: catErr } = await supabase
            .from("master_products")
            .update({ category: r.category, unit: r.unit })
            .eq("id", r.id);
          if (catErr) addLog(`(warning) couldn't update category for ${r.id}: ${catErr.message}`);
        }
      }

      // Insert the import_log row FIRST so we can stamp every supplier_price
      // with source_import_id pointing at it. That gives us provenance for
      // every CSV-imported price ("imported from <file> on <date>").
      addLog("Recording import in import_log...");
      const { data: logRow, error: logErr } = await supabase
        .from("import_log")
        .insert({
          supplier_name: supplierName,
          file_name: file.name,
          total_rows: stats.total,
          matched: 0,         // updated below after upserts complete
          updated: 0,
          flagged: stats.flagged,
        })
        .select("id")
        .single();
      // Don't hard-fail the whole import if the log insert fails — older DBs
      // may not have the table; we just lose provenance for this run.
      const importId = logRow?.id || null;
      if (logErr) addLog(`(warning) couldn't insert import_log: ${logErr.message}`);

      addLog("Updating prices...");
      const priceRows = allMatched.map((p) => ({
        master_product_id: p.master_id,
        supplier_name: supplierName,
        supplier_sku: p.sku,
        // supplier_cat_code is deliberately NOT set here. A price list's sku
        // column IS trustworthy enough to become an order code, but the gate
        // that decides lives in api/_partNumbers.js and CRA can't import from
        // api/ — a browser copy would be a second implementation to keep in
        // step on the one field where a wrong value sends someone to a counter
        // for the wrong part. The import calls backfillPartNumbers below
        // instead, so the server gate stays the only one.
        supplier_description: p.desc || null,
        unit_price: p.unit_price,
        date_recorded: new Date().toISOString().split("T")[0],
        source_type: "csv_import",
        source_import_id: importId,
      }));

      const CHUNK = 100;
      // Try with the new source_* columns first; if the migration hasn't run
      // we fall back to the legacy shape so the import still completes.
      const stripSource = (rows) => rows.map((r) => {
        const { source_type, source_import_id, ...rest } = r;
        return rest;
      });
      for (let i = 0; i < priceRows.length; i += CHUNK) {
        const slice = priceRows.slice(i, i + CHUNK);
        let { error: priceErr } = await supabase
          .from("supplier_prices")
          .upsert(slice, { onConflict: "master_product_id,supplier_name" });
        if (priceErr && /source_(type|import_id)/i.test(priceErr.message || "")) {
          ({ error: priceErr } = await supabase
            .from("supplier_prices")
            .upsert(stripSource(slice), { onConflict: "master_product_id,supplier_name" }));
        }
        if (priceErr) throw priceErr;
      }
      stats.updated = allMatched.filter((p) => p.matchType !== "new").length;
      stats.matched = allMatched.length;

      addLog("Saving aliases for future imports...");
      const aliasRows = allMatched.map((p) => ({
        master_product_id: p.master_id,
        supplier_name: supplierName,
        supplier_sku: p.sku,
      }));
      for (let i = 0; i < aliasRows.length; i += CHUNK) {
        await supabase
          .from("supplier_aliases")
          .upsert(aliasRows.slice(i, i + CHUNK), { onConflict: "supplier_name,supplier_sku" });
      }

      // Update the import_log with final counts
      if (importId) {
        await supabase
          .from("import_log")
          .update({ matched: stats.matched, updated: stats.updated })
          .eq("id", importId);
      }

      // Fill the orderable part-number column for what we just imported. The
      // gate that decides whether a price list's sku is a real order code lives
      // server-side (api/_partNumbers.js) and is the ONLY copy of that rule, so
      // the fill is a call rather than a local write. Best-effort: a failure
      // here costs part numbers on this batch, never the import itself.
      addLog("Filling supplier part numbers...");
      try {
        const partRes = await apiFetch("/api/pricebook", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "backfillPartNumbers", apply: true }),
        });
        const partData = await partRes.json();
        if (partData?.ok) {
          addLog(`Part numbers: ${partData.catCodesFilled} order codes, ${partData.mfgPartsPromoted} manufacturer parts confirmed`);
        } else {
          addLog(`(warning) part-number fill skipped: ${partData?.error || "unknown error"}`);
        }
      } catch (partErr) {
        addLog(`(warning) part-number fill skipped: ${partErr.message}`);
      }

      addLog(`Done! ${stats.matched} matched, ${stats.new} new, ${stats.updated} updated, ${stats.flagged} flagged`, "success");
      setImportStats(stats);
      setStep("done");
    } catch (err) {
      addLog(`Error: ${err.message}`, "error");
      setError(err.message);
      setStep("preview");
    }
  };

  const reset = () => {
    setStep("upload");
    setSupplier("");
    setCustomSupplier("");
    setFile(null);
    setParsed(null);
    setColMap({});
    setPreview([]);
    setImportStats(null);
    setError(null);
    setLog([]);
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        <div style={styles.header}>
          <div style={styles.headerIcon}>⚡</div>
          <div>
            <h1 style={styles.title}>Price Import</h1>
            <p style={styles.subtitle}>Upload supplier price lists — auto-matched & updated</p>
          </div>
        </div>

        <div style={styles.steps}>
          {["upload", "map", "preview", "importing", "done"].map((s, i) => {
            // Treat "extracting" as still on the upload step in the indicator.
            const visualStep = step === "extracting" ? "upload" : step;
            const order = ["upload", "map", "preview", "importing", "done"];
            return (
              <div key={s} style={styles.stepItem}>
                <div style={{
                  ...styles.stepDot,
                  background: visualStep === s ? "#f5a623" : order.indexOf(s) < order.indexOf(visualStep) ? "#4a9d5f" : "#2a2a3a"
                }} />
                {i < 4 && <div style={styles.stepLine} />}
              </div>
            );
          })}
        </div>

        {error && (
          <div style={styles.errorBanner}>⚠️ {error}</div>
        )}

        {/* ── UPLOAD ── */}
        {step === "upload" && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Select Supplier</h2>
            <div style={styles.supplierGrid}>
              {SUPPLIERS.map((s) => (
                <button
                  key={s}
                  style={{ ...styles.supplierBtn, ...(supplier === s ? styles.supplierBtnActive : {}) }}
                  onClick={() => setSupplier(s)}
                >
                  {s}
                </button>
              ))}
            </div>

            {supplier === "Other" && (
              <input
                style={styles.input}
                placeholder="Supplier name..."
                value={customSupplier}
                onChange={(e) => setCustomSupplier(e.target.value)}
              />
            )}

            {supplier && (supplier !== "Other" || customSupplier) && (
              <>
                <h2 style={{ ...styles.cardTitle, marginTop: 28 }}>Upload Price List</h2>
                <div
                  style={{ ...styles.dropzone, ...(dragOver ? styles.dropzoneActive : {}) }}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  onClick={() => fileRef.current.click()}
                >
                  <input ref={fileRef} type="file" accept=".csv,.pdf,application/pdf" style={{ display: "none" }} onChange={(e) => handleFile(e.target.files[0])} />
                  <div style={styles.dropIcon}>📂</div>
                  <p style={styles.dropText}>Drop CSV or PDF here, or click to browse</p>
                  <p style={styles.dropHint}>CSVs parse instantly. PDFs are extracted by AI (10–30s).</p>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── EXTRACTING (PDF only) ── */}
        {step === "extracting" && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Extracting from PDF…</h2>
            <p style={styles.hint}>
              Reading <strong style={{ color: "#f5a623" }}>{file?.name}</strong> with AI. This usually takes 10–30 seconds for a typical price list.
            </p>
            <div style={styles.spinner}>⏳</div>
          </div>
        )}

        {/* ── MAP COLUMNS ── */}
        {step === "map" && parsed && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Map Columns</h2>
            <p style={styles.hint}>We detected these columns from <strong style={{ color: "#f5a623" }}>{file.name}</strong>. Confirm they look right.</p>

            <div style={styles.mapGrid}>
              {[
                { key: "sku", label: "SKU / Item #", required: true },
                { key: "desc", label: "Description", required: false },
                { key: "price", label: "Unit Price", required: false },
                { key: "qty", label: "Quantity", required: false },
                { key: "total", label: "Total Amount", required: false },
              ].map(({ key, label, required }) => (
                <div key={key} style={styles.mapRow}>
                  <label style={styles.mapLabel}>
                    {label} {required && <span style={{ color: "#f5a623" }}>*</span>}
                  </label>
                  <select
                    style={styles.select}
                    value={colMap[key] || ""}
                    onChange={(e) => setColMap({ ...colMap, [key]: e.target.value || null })}
                  >
                    <option value="">— not mapped —</option>
                    {parsed.headers.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div style={styles.sampleBox}>
              <p style={styles.sampleTitle}>Sample rows from your file:</p>
              {parsed.rows.slice(0, 3).map((row, i) => (
                <div key={i} style={styles.sampleRow}>
                  <span style={{ color: "#f5a623" }}>{row[colMap.sku] || "—"}</span>
                  <span style={{ color: "#aaa" }}>{row[colMap.desc] || ""}</span>
                  <span style={{ color: "#4a9d5f" }}>${parsePrice(row[colMap.price])?.toFixed(4) || "?"}</span>
                </div>
              ))}
            </div>

            <div style={styles.btnRow}>
              <button style={styles.btnSecondary} onClick={reset}>← Back</button>
              <button style={styles.btnPrimary} onClick={buildPreview}>Preview Import →</button>
            </div>
          </div>
        )}

        {/* ── PREVIEW ── */}
        {step === "preview" && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Preview — {preview.length} products from {supplierName}</h2>

            <div style={styles.statRow}>
              {[
                { label: "Total Products", val: preview.length, color: "#f5a623" },
                { label: "Wire & Cable", val: preview.filter(p => p.category === "Wire & Cable").length, color: "#4a9d5f" },
                { label: "Breakers", val: preview.filter(p => p.category === "Breakers & Panels").length, color: "#5a8fd8" },
                { label: "Misc", val: preview.filter(p => p.category === "Misc").length, color: "#888" },
              ].map(({ label, val, color }) => (
                <div key={label} style={styles.statCard}>
                  <div style={{ ...styles.statNum, color }}>{val}</div>
                  <div style={styles.statLabel}>{label}</div>
                </div>
              ))}
            </div>

            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {["SKU", "Description", "Category", "Unit", "Price"].map((h) => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.slice(0, 50).map((p, i) => (
                    <tr key={i} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                      <td style={styles.td}><span style={styles.skuBadge}>{p.sku}</span></td>
                      <td style={{ ...styles.td, color: "#ccc" }}>{p.desc || "—"}</td>
                      <td style={styles.td}><span style={{ ...styles.catBadge, background: getCatColor(p.category) }}>{p.category}</span></td>
                      <td style={{ ...styles.td, color: "#aaa" }}>{p.unit}</td>
                      <td style={{ ...styles.td, color: "#4a9d5f", fontWeight: 600 }}>${p.unit_price.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.length > 50 && (
                <p style={styles.moreNote}>+ {preview.length - 50} more products not shown</p>
              )}
            </div>

            <div style={styles.btnRow}>
              <button style={styles.btnSecondary} onClick={() => setStep("map")}>← Back</button>
              <button style={styles.btnPrimary} onClick={runImport}>
                ⚡ Import {preview.length} Products
              </button>
            </div>
          </div>
        )}

        {/* ── IMPORTING ── */}
        {step === "importing" && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Importing...</h2>
            <div style={styles.logBox}>
              {log.map((l, i) => (
                <div key={i} style={{
                  ...styles.logLine,
                  color: l.type === "error" ? "#e05c5c" : l.type === "success" ? "#4a9d5f" : "#ccc"
                }}>
                  <span style={styles.logDot}>›</span> {l.msg}
                </div>
              ))}
              <div style={styles.spinner}>⏳</div>
            </div>
          </div>
        )}

        {/* ── DONE ── */}
        {step === "done" && importStats && (
          <div style={styles.card}>
            <div style={styles.doneIcon}>✅</div>
            <h2 style={{ ...styles.cardTitle, textAlign: "center" }}>Import Complete</h2>
            <p style={{ ...styles.hint, textAlign: "center" }}>
              {supplierName} prices are live in your price book
            </p>

            <div style={styles.statRow}>
              {[
                { label: "Total Processed", val: importStats.total, color: "#f5a623" },
                { label: "New Products", val: importStats.new, color: "#5a8fd8" },
                { label: "Duplicates", val: importStats.duplicates || 0, color: "#e0a54a" },
                { label: "Flagged", val: importStats.flagged, color: "#e05c5c" },
              ].map(({ label, val, color }) => (
                <div key={label} style={styles.statCard}>
                  <div style={{ ...styles.statNum, color }}>{val}</div>
                  <div style={styles.statLabel}>{label}</div>
                </div>
              ))}
            </div>

            {importStats.duplicateRows?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <p style={{ ...styles.sampleTitle, color: "#e0a54a", marginBottom: 8 }}>
                  {importStats.duplicateRows.length} duplicate{importStats.duplicateRows.length === 1 ? "" : "s"} — already in the price book, so the price was refreshed on the existing product (no new row added)
                </p>
                <div style={styles.tableWrap}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        {["From File", "Price", "→ Existing SKU", "Existing Description"].map((h) => (
                          <th key={h} style={styles.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importStats.duplicateRows.map((d, i) => (
                        <tr key={i} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                          <td style={styles.td}><span style={styles.skuBadge}>{d.sku}</span></td>
                          <td style={{ ...styles.td, color: "#4a9d5f", fontWeight: 600 }}>{typeof d.unit_price === "number" ? `$${d.unit_price.toFixed(4)}` : "—"}</td>
                          <td style={styles.td}><span style={styles.skuBadge}>{d.matchedSku}</span></td>
                          <td style={{ ...styles.td, color: "#ccc" }}>{d.matchedDesc || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div style={styles.logBox}>
              {log.map((l, i) => (
                <div key={i} style={{
                  ...styles.logLine,
                  color: l.type === "error" ? "#e05c5c" : l.type === "success" ? "#4a9d5f" : "#999"
                }}>
                  <span style={styles.logDot}>›</span> {l.msg}
                </div>
              ))}
            </div>

            <div style={styles.btnRow}>
              <button style={styles.btnPrimary} onClick={reset}>Import Another</button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

function getCatColor(cat) {
  const map = {
    "Wire & Cable": "#1a3a2a",
    "Breakers & Panels": "#1a2a3a",
    "Conduit": "#2a2a1a",
    "Devices & Receptacles": "#2a1a3a",
    "Lighting": "#3a2a1a",
    "Conduit Fittings & Hardware": "#1a3a3a",
    "Cable Connectors": "#3a1a1a",
    "Generators": "#3a2a3a",
    "Mini Splits": "#1a3a3a",
  };
  return map[cat] || "#2a2a2a";
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#0f0f1a",
    padding: "24px 16px 80px",
    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
    color: "#fff",
  },
  container: { maxWidth: 720, margin: "0 auto" },
  header: { display: "flex", alignItems: "center", gap: 16, marginBottom: 28 },
  headerIcon: { fontSize: 36 },
  title: { margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: -0.5 },
  subtitle: { margin: "4px 0 0", color: "#888", fontSize: 14 },
  steps: { display: "flex", alignItems: "center", marginBottom: 24 },
  stepItem: { display: "flex", alignItems: "center", flex: 1 },
  stepDot: { width: 10, height: 10, borderRadius: "50%", flexShrink: 0, transition: "background 0.3s" },
  stepLine: { flex: 1, height: 2, background: "#2a2a3a", margin: "0 4px" },
  card: { background: "#1a1a2e", borderRadius: 16, padding: 24, border: "1px solid #2a2a4a" },
  cardTitle: { margin: "0 0 16px", fontSize: 18, fontWeight: 600, color: "#fff" },
  hint: { color: "#999", fontSize: 14, marginBottom: 20, lineHeight: 1.5 },
  errorBanner: {
    background: "#2a1a1a",
    border: "1px solid #e05c5c",
    color: "#e05c5c",
    borderRadius: 10,
    padding: "12px 16px",
    marginBottom: 16,
    fontSize: 14,
  },
  supplierGrid: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 8 },
  supplierBtn: {
    padding: "10px 18px",
    borderRadius: 10,
    border: "1px solid #2a2a4a",
    background: "#12121e",
    color: "#ccc",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
    transition: "all 0.2s",
  },
  supplierBtnActive: {
    background: "#f5a623",
    color: "#000",
    border: "1px solid #f5a623",
    fontWeight: 700,
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    background: "#12121e",
    border: "1px solid #2a2a4a",
    borderRadius: 10,
    color: "#fff",
    fontSize: 15,
    marginTop: 8,
    boxSizing: "border-box",
  },
  dropzone: {
    border: "2px dashed #2a2a5a",
    borderRadius: 14,
    padding: "40px 24px",
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.2s",
    background: "#12121e",
  },
  dropzoneActive: { border: "2px dashed #f5a623", background: "#1a1510" },
  dropIcon: { fontSize: 40, marginBottom: 12 },
  dropText: { margin: "0 0 6px", fontSize: 16, fontWeight: 500 },
  dropHint: { margin: 0, color: "#666", fontSize: 13 },
  mapGrid: { display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 },
  mapRow: { display: "flex", alignItems: "center", gap: 12 },
  mapLabel: { width: 160, fontSize: 14, color: "#bbb", flexShrink: 0 },
  select: {
    flex: 1,
    padding: "10px 12px",
    background: "#12121e",
    border: "1px solid #2a2a4a",
    borderRadius: 8,
    color: "#fff",
    fontSize: 14,
  },
  sampleBox: {
    background: "#12121e",
    borderRadius: 10,
    padding: "14px 16px",
    marginBottom: 20,
    border: "1px solid #2a2a3a",
  },
  sampleTitle: { margin: "0 0 10px", color: "#666", fontSize: 12, textTransform: "uppercase", letterSpacing: 1 },
  sampleRow: { display: "flex", gap: 16, padding: "4px 0", fontSize: 13 },
  statRow: { display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" },
  statCard: {
    flex: 1,
    minWidth: 100,
    background: "#12121e",
    borderRadius: 10,
    padding: "14px",
    border: "1px solid #2a2a3a",
    textAlign: "center",
  },
  statNum: { fontSize: 24, fontWeight: 700, marginBottom: 4 },
  statLabel: { fontSize: 12, color: "#666" },
  tableWrap: { overflowX: "auto", marginBottom: 20, borderRadius: 10, border: "1px solid #2a2a3a" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { padding: "10px 12px", background: "#12121e", color: "#888", textAlign: "left", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 },
  trEven: { background: "#12121e" },
  trOdd: { background: "#0f0f18" },
  td: { padding: "8px 12px", color: "#ddd", borderTop: "1px solid #1e1e2e" },
  skuBadge: { fontFamily: "monospace", fontSize: 12, color: "#f5a623" },
  catBadge: { padding: "2px 8px", borderRadius: 6, fontSize: 11, color: "#aaa" },
  moreNote: { textAlign: "center", color: "#555", padding: "12px", fontSize: 13 },
  logBox: {
    background: "#0a0a14",
    borderRadius: 10,
    padding: "16px",
    fontFamily: "monospace",
    fontSize: 13,
    maxHeight: 260,
    overflowY: "auto",
    marginBottom: 20,
    border: "1px solid #1e1e2e",
  },
  logLine: { padding: "3px 0", lineHeight: 1.5 },
  logDot: { color: "#f5a623", marginRight: 8 },
  spinner: { textAlign: "center", marginTop: 12, fontSize: 20 },
  doneIcon: { textAlign: "center", fontSize: 48, marginBottom: 12 },
  btnRow: { display: "flex", gap: 12, justifyContent: "flex-end" },
  btnPrimary: {
    padding: "12px 24px",
    background: "#f5a623",
    color: "#000",
    border: "none",
    borderRadius: 10,
    fontWeight: 700,
    fontSize: 15,
    cursor: "pointer",
  },
  btnSecondary: {
    padding: "12px 24px",
    background: "transparent",
    color: "#aaa",
    border: "1px solid #2a2a4a",
    borderRadius: 10,
    fontWeight: 500,
    fontSize: 15,
    cursor: "pointer",
  },
};
